import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import Button from 'components/Button/Button';
import { useModalRegistration } from 'views/ModalRegistryProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faIdBadge, faUserCheck } from '@fortawesome/pro-duotone-svg-icons';
import headerCheckerboard from 'assets/logo/header-checkerboard-background-checks.svg';
import moment, { Moment } from 'moment';

export type ExpiryKind = 'dbs' | 'id';

const useStyles = createUseStyles((theme: Theme) => ({
  '@keyframes overlayIn': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  },
  '@keyframes panelIn': {
    from: { opacity: 0, transform: 'scale(0.94) translateY(24px)' },
    to: { opacity: 1, transform: 'scale(1) translateY(0)' }
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '12vh',
    background: 'rgba(23, 25, 35, 0.18)',
    animation: '$overlayIn 0.15s ease-out'
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    width: 480,
    maxWidth: 'calc(100vw - 40px)',
    maxHeight: '80vh',
    background: 'white',
    borderRadius: 14,
    border: '1px solid #ededed',
    boxShadow: '0 16px 70px rgba(0, 0, 0, 0.2), 0 3px 12px rgba(0, 0, 0, 0.08)',
    animation: '$panelIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    overflow: 'hidden'
  },
  cover: {
    position: 'relative',
    flexShrink: 0,
    height: 84,
    background: `
      url(${headerCheckerboard}) left top / auto 100% no-repeat,
      linear-gradient(135deg, #f4f7ff, #eef3ff)
    `,
    borderBottom: '1px solid #ededed'
  },
  closeButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: 'white',
    border: '1px solid #ededed',
    cursor: 'pointer',
    color: '#74777f',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f0f1f3'
    }
  },
  medallionRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: -34,
    zIndex: 1
  },
  medallion: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 68,
    height: 68,
    borderRadius: '50%',
    background: 'white',
    border: '1px solid #ededed',
    boxShadow: '0 4px 14px rgba(11, 87, 255, 0.14)',
    color: '#0b57ff',
    fontSize: 26
  },
  titleBlock: {
    padding: '12px 28px 18px',
    textAlign: 'center'
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
    color: theme.colors.primaryBlack
  },
  subtitle: {
    marginTop: 4,
    fontSize: 13,
    color: '#74777f'
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 28px 8px'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 18
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: '#74777f',
    marginBottom: 7
  },
  labelHint: {
    fontWeight: 400,
    color: '#9ea2ad'
  },
  dateInput: {
    boxSizing: 'border-box',
    minHeight: 40,
    padding: '0 14px',
    fontSize: 14,
    color: theme.colors.primaryBlack,
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus': {
      borderColor: '#0b57ff',
      boxShadow: '0 0 0 3px rgba(11, 87, 255, 0.1)',
      outline: 'none'
    }
  },
  datePill: {
    display: 'flex',
    alignItems: 'stretch',
    border: '1px solid #E0E3E7',
    borderRadius: 999,
    overflow: 'hidden',
    marginBottom: 18
  },
  pillHalf: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    padding: '12px 22px',
    background: 'white',
    transition: 'background-color 0.15s'
  },
  pillHalfPassive: {
    extend: 'pillHalf',
    background: '#f8f9fb',
    cursor: 'pointer',
    '& $pillLabel': {
      color: '#9ea2ad'
    },
    '& $pillInput': {
      color: '#9ea2ad'
    },
    '&:hover': {
      background: '#f1f3f7'
    }
  },
  pillDivider: {
    width: 1,
    background: '#E0E3E7',
    flexShrink: 0
  },
  pillLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#74777f'
  },
  pillInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: 0,
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.primaryBlack,
    fontFamily: 'inherit',
    width: '100%'
  },
  pillHint: {
    marginTop: -10,
    marginBottom: 18,
    fontSize: 12.5,
    color: '#9ea2ad',
    textAlign: 'center'
  },
  optionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '14px 16px',
    border: '1px solid #ededed',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'border-color 0.15s, background-color 0.15s'
  },
  optionRowChecked: {
    extend: 'optionRow',
    borderColor: '#0b57ff',
    background: 'rgba(11, 87, 255, 0.05)'
  },
  checkbox: {
    width: 18,
    height: 18,
    marginTop: 1,
    flexShrink: 0,
    accentColor: '#0b57ff',
    cursor: 'pointer'
  },
  optionLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.primaryBlack
  },
  optionHint: {
    fontSize: 13,
    color: '#74777f',
    marginTop: 2,
    lineHeight: 1.35
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '18px 28px',
    borderTop: '1px solid #ededed',
    marginTop: 20
  }
}));

// --- Shared shell -----------------------------------------------------------

type ShellProps = {
  isOpen: boolean;
  onClose: () => void;
  icon: IconProp;
  title: string;
  subtitle: string;
  canSubmit?: boolean;
  onSubmit?: () => void;
  submitLabel?: string;
  width?: number;
  children: React.ReactNode;
};

export function ExpiryModalShell({
  isOpen,
  onClose,
  icon,
  title,
  subtitle,
  canSubmit,
  onSubmit,
  submitLabel = 'Save expiry date',
  width,
  children
}: ShellProps) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  useModalRegistration(isOpen);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={classes.overlay} onClick={onClose}>
      <div
        className={classes.panel}
        style={width ? { width } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={classes.cover}>
          <div className={classes.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </div>
        </div>
        <div className={classes.medallionRow}>
          <div className={classes.medallion}>
            <FontAwesomeIcon icon={icon} />
          </div>
        </div>
        <div className={classes.titleBlock}>
          <div className={classes.title}>{title}</div>
          <div className={classes.subtitle}>{subtitle}</div>
        </div>
        <div className={classes.body}>{children}</div>
        <div className={classes.footer}>
          <Button archetype="flat" onClick={onClose}>
            {onSubmit ? 'Cancel' : 'Close'}
          </Button>
          {onSubmit && (
            <Button
              archetype="flatPrimary"
              disabled={!canSubmit}
              onClick={onSubmit}
            >
              {submitLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

type ConfirmRowProps = {
  confirmed: boolean;
  setConfirmed: (value: boolean) => void;
};

function ConfirmRow({ confirmed, setConfirmed }: ConfirmRowProps) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  return (
    <div
      className={confirmed ? classes.optionRowChecked : classes.optionRow}
      onClick={() => setConfirmed(!confirmed)}
    >
      <input
        type="checkbox"
        className={classes.checkbox}
        checked={confirmed}
        onChange={(e) => setConfirmed(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
      />
      <div>
        <div className={classes.optionLabel}>
          I confirm I have seen and verified this document
        </div>
        <div className={classes.optionHint}>
          You have checked the original document and the dates entered match it.
        </div>
      </div>
    </div>
  );
}

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (expiryDate: Moment) => void;
};

const parseDate = (value: string) =>
  value ? moment.utc(value, 'YYYY-MM-DD') : undefined;

// --- ID expiry --------------------------------------------------------------

export function IDExpiryModal({ isOpen, onClose, onSubmit }: ModalProps) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const [expiryStr, setExpiryStr] = React.useState('');
  const [confirmed, setConfirmed] = React.useState(false);

  // Reset the form whenever it opens
  React.useEffect(() => {
    if (!isOpen) return;
    setExpiryStr('');
    setConfirmed(false);
  }, [isOpen]);

  const expiry = parseDate(expiryStr);
  const canSubmit = !!expiry && expiry.isValid() && confirmed;

  return (
    <ExpiryModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={faUserCheck}
      title="Add ID expiry date"
      subtitle="Enter the expiry date shown on the ID document - passport, driving licence, etc."
      canSubmit={canSubmit}
      onSubmit={() => expiry && onSubmit(expiry)}
    >
      <div className={classes.field}>
        <span className={classes.label}>ID expiry date</span>
        <input
          type="date"
          className={classes.dateInput}
          value={expiryStr}
          onChange={(e) => setExpiryStr(e.target.value)}
        />
      </div>
      <ConfirmRow confirmed={confirmed} setConfirmed={setConfirmed} />
    </ExpiryModalShell>
  );
}

// --- DBS expiry -------------------------------------------------------------

// DBS certificates only carry an issue date — the expiry is company policy:
// three years minus a day from issue. The pill has an issue half and an
// expiry half; whichever is active drives, and the passive (greyed) half
// live-updates with the offset value. Clicking the passive half swaps them.
export function DBSExpiryModal({ isOpen, onClose, onSubmit }: ModalProps) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const [activeSide, setActiveSide] = React.useState<'issue' | 'expiry'>(
    'issue'
  );
  const [issueStr, setIssueStr] = React.useState('');
  const [expiryStr, setExpiryStr] = React.useState('');
  const [confirmed, setConfirmed] = React.useState(false);

  const issueRef = React.useRef<HTMLInputElement>(null);
  const expiryRef = React.useRef<HTMLInputElement>(null);

  const swapTo = (side: 'issue' | 'expiry') => {
    setActiveSide(side);
    // Open the native picker on the newly active input once it's editable.
    // showPicker needs a user gesture — this runs within the click's window.
    requestAnimationFrame(() => {
      const el = side === 'issue' ? issueRef.current : expiryRef.current;
      if (!el) return;
      el.focus();
      try {
        (el as any).showPicker?.();
      } catch {
        // Some browsers refuse outside a direct gesture — focus is enough
      }
    });
  };

  // Reset the form whenever it opens
  React.useEffect(() => {
    if (!isOpen) return;
    setActiveSide('issue');
    setIssueStr('');
    setExpiryStr('');
    setConfirmed(false);
  }, [isOpen]);

  const onIssueChange = (value: string) => {
    setIssueStr(value);
    const issue = parseDate(value);
    setExpiryStr(
      issue && issue.isValid()
        ? issue.clone().add(3, 'years').subtract(1, 'day').format('YYYY-MM-DD')
        : ''
    );
  };

  const onExpiryChange = (value: string) => {
    setExpiryStr(value);
    const expiry = parseDate(value);
    setIssueStr(
      expiry && expiry.isValid()
        ? expiry.clone().subtract(3, 'years').add(1, 'day').format('YYYY-MM-DD')
        : ''
    );
  };

  const expiry = parseDate(expiryStr);
  const canSubmit = !!expiry && expiry.isValid() && confirmed;

  const half = (side: 'issue' | 'expiry') => {
    const isActive = activeSide === side;
    const value = side === 'issue' ? issueStr : expiryStr;
    const onChange = side === 'issue' ? onIssueChange : onExpiryChange;

    return (
      <div
        className={isActive ? classes.pillHalf : classes.pillHalfPassive}
        onClick={isActive ? undefined : () => swapTo(side)}
      >
        <span className={classes.pillLabel}>
          {side === 'issue' ? 'Issue date' : 'Expiry date'}
        </span>
        <input
          type="date"
          className={classes.pillInput}
          ref={side === 'issue' ? issueRef : expiryRef}
          value={value}
          readOnly={!isActive}
          tabIndex={isActive ? 0 : -1}
          style={isActive ? undefined : { pointerEvents: 'none' }}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  };

  return (
    <ExpiryModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={faIdBadge}
      title="Add DBS expiry date"
      subtitle="Enter the issue date from the DBS certificate, or set the expiry date directly"
      canSubmit={canSubmit}
      onSubmit={() => expiry && onSubmit(expiry)}
    >
      <div className={classes.datePill}>
        {half('issue')}
        <div className={classes.pillDivider} />
        {half('expiry')}
      </div>
      <div className={classes.pillHint}>
        Expiry is 3 years minus a day from issue - click the greyed side to
        enter that date instead
      </div>
      <ConfirmRow confirmed={confirmed} setConfirmed={setConfirmed} />
    </ExpiryModalShell>
  );
}
